<template>
  <div v-if="invoice">
    <div>
      <ActionsMenu
        :options="tippyOptions"
        :actions="actions"
        @approve-invoice-click="
          visibleActionKeys.includes('approve') && handleApproveInvoice()
        "
        @accept-payment-click="
          visibleActionKeys.includes('accept_payment') &&
            toggleModal('paymentModal')
        "
        @refund-click="
          visibleActionKeys.includes('issue_refund') &&
            toggleModal('refundModal')
        "
        @edit-date-click="
          visibleActionKeys.includes('edit_date') &&
            toggleModalWithWarnings('editDateModal')
        "
        @download-click="
          visibleActionKeys.includes('download') && handleDownload()
        "
        @resubmit-invoice="
          visibleActionKeys.includes('resubmit_for_payment') &&
            toggleModal('resubmitModal')
        "
        @email-invoice-click="
          visibleActionKeys.includes('email') && handleEmailInvoice()
        "
        @unapprove-invoice-click="
          visibleActionKeys.includes('unapprove') &&
            toggleModalWithWarnings('unapproveModal')
        "
        @void-invoice-click="
          visibleActionKeys.includes('void') && toggleModal('voidModal')
        "
        @resume-batch-click="handleResumeBatch()"
        @do-not-batch-click="handleDoNotBatch()"
      >
        <template #trigger>
          <SeraButton
            v-if="hasButtonMenu"
            text="Invoice Actions"
            icon="fa-regular fa-chevron-down"
            icon-position="right"
            data-cy="invoice-actions"
          />
        </template>
        <template #footer>
          <div
            v-if="allowToggleDisplayMembershipSavings"
            class="border-t-[1px] border-gray-300"
          >
            <div
              class="flex justify-between align-center whitespace-nowrap px-5 py-4"
            >
              <span class="!text-gray-700 font-medium text-[12px]"
                >Show Membership Savings</span
              >
              <ToggleSwitch
                v-model="displayMembershipSavings"
                data-cy="display-membership-savings-toggle"
                @update:modelValue="handleToggleMembershipSavings"
              />
            </div>
          </div>
        </template>
      </ActionsMenu>
    </div>
    <AcceptPayment
      v-if="paymentModal"
      :invoice="invoice"
      :disabled="invoice.financials.balance_cents <= 0"
      @hide-modal="toggleModal('paymentModal')"
    />
    <WorkCompleteModal
      v-if="signatureModal"
      :invoice="invoice"
      @hide-modal="toggleModal('signatureModal')"
    />
    <RefundModal
      v-if="refundModal"
      :invoice="invoice"
      @hide-modal="toggleModal('refundModal')"
    />
    <ActionConfirmationModal
      v-if="resubmitModal"
      :modal-contents="resubmitModalContents"
      @hide-modal="toggleModal('resubmitModal')"
    />
    <EditInvoiceDateModal
      v-if="editDateModal"
      :invoice="invoice"
      @hide-modal="toggleModal('editDateModal')"
    />
    <EmailQuoteOrInvoiceModal
      v-if="showEmailInvoiceModal"
      :invoice="invoice"
      @hide-modal="showEmailInvoiceModal = false"
    />
    <VoidInvoiceModal
      v-if="voidModal"
      :invoice="invoice"
      @hide-modal="toggleModal('voidModal')"
    />
    <UnapproveInvoiceModal
      v-if="unapproveModal"
      :invoice="invoice"
      @hide-modal="toggleModal('unapproveModal')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import ActionsMenu from "@/components/ActionsMenu.vue";
import SeraButton from "@/components/Button.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import AcceptPayment from "@/AcceptPayment/AcceptPayment.vue";
import EmailQuoteOrInvoiceModal from "@/EmailQuoteOrInvoice/EmailQuoteOrInvoiceModal.vue";
import EditInvoiceDateModal from "@/Invoice/EditInvoiceDateModal.vue";
import RefundModal from "@/Invoice/RefundModal.vue";
import UnapproveInvoiceModal from "@/Invoice/UnapproveInvoiceModal.vue";
import VoidInvoiceModal from "@/Invoice/VoidInvoiceModal.vue";
import WorkCompleteModal from "@/Invoice/WorkCompleteModal.vue";
import { jobWarnings } from "@/mixins/jobWarnings";
import ActionConfirmationModal from "@/Reports/ActionConfirmationModal.vue";

export default {
  components: {
    EmailQuoteOrInvoiceModal,
    ActionsMenu,
    SeraButton,
    AcceptPayment,
    WorkCompleteModal,
    RefundModal,
    ActionConfirmationModal,
    EditInvoiceDateModal,
    UnapproveInvoiceModal,
    VoidInvoiceModal,
    ToggleSwitch
  },
  mixins: [jobWarnings],
  props: {
    invoice: {
      type: Object,
      default: null
    },
    // If true the SeraButton is shown otherwise three vertical dots.
    hasButtonMenu: {
      type: Boolean,
      default: false
    },
    tippyOptions: {
      type: Object,
      default: null
    },
    // Determines which menu actions are shown.
    visibleActionKeys: {
      type: Array,
      default: null
    },
    hideMembershipSavingsToggle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showEmailInvoiceModal: false,
      paymentModal: false,
      signatureModal: false,
      refundModal: false,
      resubmitModal: false,
      editSummaryModal: false,
      editDateModal: false,
      unapproveModal: false,
      voidModal: false,

      displayMembershipSavings: this.invoice.display_membership_savings
    };
  },
  computed: {
    ...mapState(["job", "tenant"]),
    actions() {
      const activeActions = [];
      if (this.visibleActionKeys.includes("approve")) {
        activeActions.push({
          label: "Approve Invoice",
          icon: "fa-regular fa-thumbs-up",
          eventName: "approve-invoice-click",
          className: "menu-item",
          disabled: !this.invoice.links.invoice_approve
        });
      }
      if (this.visibleActionKeys.includes("accept_payment")) {
        activeActions.push({
          label: "Accept Payment",
          icon: "fa-regular fa-credit-card",
          eventName: "accept-payment-click",
          className: "menu-item",
          disabled: !this.invoice.links.payment_create
        });
      }
      if (this.visibleActionKeys.includes("issue_refund")) {
        activeActions.push({
          label: "Issue Refund",
          icon: "fa-regular fa-money-check-edit-alt",
          eventName: "refund-click",
          className: "menu-item",
          disabled: !this.invoice.links.invoice_refund
        });
      }
      if (this.visibleActionKeys.includes("edit_details")) {
        let options = {};
        if (!this.invoice.links.edit_details) {
          options = { label: "Edit Details", html: null };
        }
        activeActions.push({
          icon: "fa-regular fa-calendar-lines-pen",
          eventName: "edit-details-click",
          className: "menu-item action-link-wrapper",
          disabled: !this.invoice.links.edit_details,
          html: `<a href="${this.invoice.links.edit_details}"
            class="action-link"
            data-turbo-frame="slide_panel"
            data-action="slide-panel#open"
            data-slide-panel-title-param="Edit Invoice Details">
              Edit Details
          </a>`,
          ...options
        });
      }
      if (this.visibleActionKeys.includes("edit_date")) {
        activeActions.push({
          label: "Edit Date",
          icon: "fa-regular fa-calendar-lines-pen",
          eventName: "edit-date-click",
          className: "menu-item",
          disabled: !this.invoice.links.edit_invoice_date,
          tooltip: !this.invoice.links.edit_invoice_date
            ? "Invoice must be approved before date can be edited."
            : ""
        });
      }
      if (this.visibleActionKeys.includes("unapprove")) {
        activeActions.push({
          label: "Edit Invoice",
          icon: "far fa-pen-to-square",
          eventName: "unapprove-invoice-click",
          className: "menu-item",
          disabled: !this.invoice.links.invoice_break
        });
      }
      if (this.visibleActionKeys.includes("void")) {
        activeActions.push({
          label: "Void Invoice",
          icon: "fa-regular fa-ban",
          eventName: "void-invoice-click",
          className: "menu-item",
          disabled: !this.invoice.links.void
        });
      }
      if (this.visibleActionKeys.includes("batch")) {
        if (this.invoice.exclude_from_batching) {
          activeActions.push({
            label: "Resume Batching",
            icon: "fa-regular fa-badge-check",
            eventName: "resume-batch-click",
            className: "menu-item"
          });
        } else {
          activeActions.push({
            label: "Do Not Batch",
            icon: "fa-regular fa-file-circle-xmark",
            eventName: "do-not-batch-click",
            className: "menu-item"
          });
        }
      }
      if (this.visibleActionKeys.includes("download")) {
        activeActions.push({
          label: "Download Invoice",
          icon: "fa-regular fa-download",
          eventName: "download-click",
          className: "menu-item",
          disabled: !this.invoice.links.invoice_pdf
        });
      }
      if (this.visibleActionKeys.includes("resubmit_for_payment")) {
        activeActions.push({
          label: "Resubmit for Payment",
          icon: "fa-regular fa-credit-card",
          eventName: "resubmit-invoice",
          className: "menu-item",
          disabled: !this.invoice.links.resubmit
        });
      }
      if (this.visibleActionKeys.includes("email")) {
        activeActions.push({
          label: "Email",
          icon: "fa-regular fa-envelope",
          eventName: "email-invoice-click",
          className: "menu-item",
          disabled: !this.invoice.links.invoice_email
        });
      }

      return activeActions;
    },
    resubmitModalContents() {
      if (!this.invoice) {
        return {};
      }
      return {
        message:
          "Resubmitting this invoice for billing will attempt to collect payment with the payment method on file.",
        title: "Confirm Rebilling",
        success_message: `Invoice #${this.invoice.invoice_id} submitted for rebilling.  Please check the status in a few minutes.`,
        event_name: "invoice-resubmitted",
        action_url: this.invoice.links.resubmit,
        hide_input: true
      };
    },
    allowToggleDisplayMembershipSavings() {
      return (
        this.featureEnabledForTenant("toggle_display_membership_savings") &&
        this.tenant.display_membership_savings_ad_hoc &&
        !this.hideMembershipSavingsToggle &&
        !this.invoice.finalized &&
        this.invoice.links.update_display_membership_savings
      );
    }
  },
  methods: {
    ...mapActions([
      "confirmChanges",
      "fetchInvoice",
      "updateInvoiceBatching",
      "updateInvoiceDisplayMembershipSavings"
    ]),
    ...mapGetters(["featureEnabledForTenant"]),
    handleDownload() {
      window.open(this.invoice.pdf_url);
    },
    toggleModalWithWarnings(modal) {
      this.withJobWarnings({
        action: () => this.toggleModal(modal),
        record: this.invoice,
        context: "edit-invoice"
      });
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    async handleApproveInvoice() {
      const hasFulfilledAllItems = !this.invoice.items_attributes.find(
        (item) => item.equipment_request_status === "unfulfilled"
      );
      if (hasFulfilledAllItems) {
        this.toggleModal("signatureModal");
        return;
      }
      await this.confirmChanges({
        title: "Equipment Requested",
        message:
          "There are items that require equipment to be captured.<br/> Are you sure you wish to continue?"
      });
      this.toggleModal("signatureModal");
    },
    handleEmailInvoice() {
      this.showEmailInvoiceModal = true;
    },
    async handleResumeBatch() {
      try {
        await this.updateInvoiceBatching({
          link: this.invoice.links.update_batching,
          exclude_from_batching: false
        });
        this.fetchInvoice(this.invoice.invoice_id);
        window.notify.success("Batching has been resumed for this invoice.");
      } catch (e) {
        window.notify.error(
          "An error occurred when attempting to resume batching."
        );
      }
    },
    async handleDoNotBatch() {
      await this.confirmChanges({
        title: "Do Not Batch Invoice",
        message:
          "This will disable batching this invoice to your accounting system.<br/> Are you sure you wish to continue?"
      });
      try {
        await this.updateInvoiceBatching({
          link: this.invoice.links.update_batching,
          exclude_from_batching: true
        });
        this.fetchInvoice(this.invoice.invoice_id);
        window.notify.success("Batching has been stopped for this invoice.");
      } catch (e) {
        window.notify.error(
          "An error occurred when attempting to stop batching."
        );
      }
    },
    handleToggleMembershipSavings() {
      this.updateInvoiceDisplayMembershipSavings({
        url: this.invoice.links.update_display_membership_savings,
        display_membership_savings: this.displayMembershipSavings
      }).then(() => {
        window.notify.success(
          "Successfully updated membership savings configuration."
        );
      });
    }
  }
};
</script>

<style lang="scss" scoped></style>
